import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePublishCrumbs } from './shell-slot.js';
import { OnSurface } from './surface.js';

/**
 * Page-level layout primitives.
 *
 * Responsive rules, applied everywhere so pages do not each choose their own:
 *  - Content is capped at a readable measure and centred, with `mx-auto`.
 *  - Horizontal padding grows with the viewport rather than jumping.
 *  - Nothing is positioned with `left`/`right`. Every inset, margin and
 *    alignment here is LOGICAL, so the whole page mirrors under Arabic without
 *    a second stylesheet.
 *  - Breakpoints: base is the phone layout, `sm` the large phone / small
 *    tablet, `md` the tablet, `lg` the desktop. A page is built at the phone
 *    width first and allowed to spread, which is why nothing collapses at the
 *    small end.
 */

export function PageContainer({
  children,
  width = 'wide',
}: {
  readonly children: ReactNode;
  /** `narrow` for a single form; `wide` for a list or a dashboard. */
  readonly width?: 'narrow' | 'wide';
}) {
  return (
    <div
      className={`mx-auto w-full px-4 py-6 sm:px-6 lg:px-8 lg:py-8 ${
        width === 'narrow' ? 'max-w-3xl' : 'max-w-6xl'
      }`}
    >
      {children}
    </div>
  );
}

export interface Crumb {
  /** Already translated. */
  readonly label: string;
  /** Omitted for the current page, which is not a link. */
  readonly to?: string;
}

/**
 * Breadcrumbs.
 *
 * A real `nav` with an ordered list, because the order is the meaning. The
 * separator is `aria-hidden` — a screen reader gets the list structure, and
 * hearing "slash" between every level is noise.
 *
 * The separator is a slash rather than a chevron precisely because a slash does
 * not point anywhere: a chevron would have to be mirrored under RTL, and a
 * character that carries no direction cannot be mirrored wrongly.
 */
export function Breadcrumbs({ items }: { items: readonly Crumb[] }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  return (
    <nav aria-label={t('common.breadcrumbs')} className="min-w-0">
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-caption text-default-600">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-1.5">
              {item.to && !isLast ? (
                <Link
                  to={item.to}
                  className="rounded-sm text-default-600 no-underline underline-offset-2 hover:text-default-900 hover:underline"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  {...(isLast ? { 'aria-current': 'page' as const } : {})}
                  className={isLast ? 'font-semibold text-default-900' : undefined}
                >
                  {item.label}
                </span>
              )}
              {isLast ? null : (
                <span aria-hidden="true" className="text-default-300">
                  /
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Standard page heading.
 *
 * `ms-auto` on the action group is a logical property: actions sit at the
 * trailing edge, which mirrors correctly between Arabic and English without
 * either language needing its own stylesheet.
 *
 * BREADCRUMBS GO TO THE TOP BAR (D278). Inside the application shell the
 * header publishes them to the shell's breadcrumb slot and does not draw them
 * again; outside it (public pages, the portal, a test) it draws them here.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumbs,
}: {
  /** Already translated. */
  readonly title: string;
  readonly subtitle?: string;
  readonly actions?: ReactNode;
  readonly breadcrumbs?: readonly Crumb[];
}) {
  const inShell = usePublishCrumbs(breadcrumbs);
  return (
    <header className="mb-stack">
      {breadcrumbs && !inShell ? (
        <div className="mb-stack">
          <Breadcrumbs items={breadcrumbs} />
        </div>
      ) : null}
      <div className="flex flex-wrap items-start gap-x-stack gap-y-3">
        {/*
          `basis-48`, not `flex-1`: a zero basis let the title shrink to nothing
          instead of letting the actions wrap, so at phone width a page with two
          actions (a report template) set its title one word per line beside
          them. With a real basis the actions move below the title once the
          title would have less than 12rem — found in the Phase 10 browser check.
        */}
        <div className="min-w-0 grow basis-48">
          <h1 className="text-title font-bold text-default-900">{title}</h1>
          {subtitle ? (
            <p className="mt-0.5 text-body text-default-600">{subtitle}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-inline">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}

/**
 * A bounded surface for a block of content.
 *
 * Used instead of each page inventing its own border and padding, which is how
 * two screens end up looking like two products.
 *
 * The approved redesign's "box" (D278): a white sheet with a hairline border;
 * when it has a title, the title sits in a header strip divided from the body
 * by a hairline, with any actions at the strip's trailing end.
 */
export function Panel({
  title,
  description,
  actions,
  children,
}: {
  /** Already translated. */
  readonly title?: string;
  readonly description?: string;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section className="min-w-0 rounded-large border border-default-200 bg-white">
      {title ? (
        <header className="flex flex-wrap items-center gap-x-stack gap-y-1 border-b border-default-200 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            {/* The same weight and ink as a form section's legend, so a panel
                and a section on one page read as one hierarchy. */}
            <h2 className="text-body font-bold text-default-900">{title}</h2>
            {description ? (
              <p className="text-caption text-default-600">{description}</p>
            ) : null}
          </div>
          {actions}
        </header>
      ) : null}
      <div className="p-4">
        <OnSurface>{children}</OnSurface>
      </div>
    </section>
  );
}
